import React, { useState } from 'react';
import { PhoneCall, MapPin, Building, Send, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';
import { B2BContactPayload } from '../data/types.js';
import { submitCommercialForm } from '../services/websiteFormsService.js';

interface B2BContactFormProps {
  isEn?: boolean;
}

export const B2BContactForm: React.FC<B2BContactFormProps> = ({ isEn = false }) => {
  const c = isEn ? contentEn : contentEs;
  const f = c.business.contact.form;

  const [formData, setFormData] = useState<B2BContactPayload & { _hp?: string }>({
    empresa: '',
    nombre: '',
    correo: '',
    telefono: '',
    ciudad: isEn ? 'Mexico City' : 'Ciudad de México',
    personas: '',
    ubicaciones: '',
    tipoServicio: f.tipoServicioOptions[0],
    mensaje: '',
    _hp: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!formData.empresa.trim() || !formData.nombre.trim() || !formData.correo.trim()) {
      setErrorMsg(isEn ? 'Please fill in Company, Name and Corporate Email.' : 'Por favor completa los campos obligatorios: Empresa, Nombre y Correo.');
      return;
    }

    if (!formData.correo.includes('@') || !formData.correo.includes('.')) {
      setErrorMsg(isEn ? 'Please enter a valid email address.' : 'Por favor introduce un correo electrónico válido.');
      return;
    }

    setSubmitting(true);
    const res = await submitCommercialForm(formData);
    setSubmitting(false);

    if (res.success) {
      setSubmitted(true);
      setFormData({
        empresa: '',
        nombre: '',
        correo: '',
        telefono: '',
        ciudad: isEn ? 'Mexico City' : 'Ciudad de México',
        personas: '',
        ubicaciones: '',
        tipoServicio: f.tipoServicioOptions[0],
        mensaje: '',
        _hp: '',
      });
    } else {
      setErrorMsg(res.error || (isEn ? 'Could not submit request. Please try again.' : 'No se pudo enviar la solicitud comercial. Por favor intenta de nuevo.'));
    }
  };

  return (
    <section id="contacto" className="bg-[#001557] text-white py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 rounded-full bg-sky-400/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 space-y-8">
            <div>
              <span className="text-xs uppercase font-extrabold tracking-widest text-[#93C5FD]">
                {c.business.contact.eyebrow}
              </span>
              <h2 className="mt-2 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                {c.business.contact.title}
              </h2>
              <p className="mt-4 text-base sm:text-lg text-[#BFDBFE] leading-relaxed">
                {c.business.contact.body}
              </p>
            </div>

            <div className="space-y-6 pt-4 border-t border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-white/10 text-white shrink-0">
                  <PhoneCall className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-base text-white">
                    {c.business.contact.points[0].title}
                  </h4>
                  <p className="text-sm text-[#93C5FD] mt-0.5">
                    {c.business.contact.points[0].desc}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-white/10 text-white shrink-0">
                  <Building className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-base text-white">
                    {c.business.contact.points[1].title}
                  </h4>
                  <p className="text-sm text-[#93C5FD] mt-0.5">
                    {c.business.contact.points[1].desc}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-white/10 text-white shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-base text-white">
                    {c.business.contact.points[2].title}
                  </h4>
                  <p className="text-sm text-[#93C5FD] mt-0.5">
                    {c.business.contact.points[2].desc}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white text-gray-900 rounded-2xl p-6 sm:p-10 shadow-2xl border border-gray-100">
              <div className="mb-6">
                <h3 className="font-heading font-black text-2xl text-[#001557]">
                  {isEn ? 'Tell us about your operation' : 'Cuéntanos de tu operación'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {isEn 
                    ? 'Fill out the form below and our team will get in touch.' 
                    : 'Completa la información y nuestro equipo comercial dará seguimiento a tu solicitud.'}
                </p>
              </div>

              {submitted ? (
                <div className="py-10 text-center space-y-4">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h4 className="font-heading font-black text-2xl text-[#001557]">
                    {isEn ? 'Request Received!' : '¡Solicitud Recibida!'}
                  </h4>
                  <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                    {isEn 
                      ? 'Thank you for reaching out. Our commercial team will review your requirements and follow up shortly.'
                      : '¡Gracias por escribirnos! Hemos recibido tu solicitud comercial y nuestro equipo se pondrá en contacto a la brevedad.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#001557] underline hover:text-[#97472E]"
                  >
                    {isEn ? 'Send another request' : 'Enviar otra solicitud'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="_hp"
                    value={formData._hp}
                    onChange={(e) => setFormData({ ...formData, _hp: e.target.value })}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  {errorMsg && (
                    <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.empresa} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.empresa}
                        onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                        placeholder={f.empresaPlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.nombre} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder={f.nombrePlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.correo} *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.correo}
                        onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                        placeholder={f.correoPlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.telefono}
                      </label>
                      <input
                        type="tel"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder={f.telefonoPlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.ciudad}
                      </label>
                      <select
                        value={formData.ciudad}
                        onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      >
                        <option value="CDMX">{isEn ? 'Mexico City' : 'Ciudad de México'}</option>
                        <option value="EDOMEX">{isEn ? 'State of Mexico' : 'Estado de México'}</option>
                        <option value="Otra">{isEn ? 'Other location' : 'Otra ubicación'}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.personas}
                      </label>
                      <input
                        type="text"
                        value={formData.personas}
                        onChange={(e) => setFormData({ ...formData, personas: e.target.value })}
                        placeholder={f.personasPlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {f.ubicaciones}
                      </label>
                      <input
                        type="text"
                        value={formData.ubicaciones}
                        onChange={(e) => setFormData({ ...formData, ubicaciones: e.target.value })}
                        placeholder={f.ubicacionesPlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {f.tipoServicio}
                    </label>
                    <select
                      value={formData.tipoServicio}
                      onChange={(e) => setFormData({ ...formData, tipoServicio: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    >
                      {f.tipoServicioOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {f.mensaje}
                    </label>
                    <textarea
                      rows={3}
                      value={formData.mensaje}
                      onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                      placeholder={f.mensajePlaceholder}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557] resize-none"
                    />
                  </div>

                  {/* BOTÓN PRINCIPAL SIN WHATSAPP, EQUILIBRADO Y ANCHO COMPLETO */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#001557] hover:bg-[#11255E] text-white font-bold py-3.5 px-6 rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>{isEn ? 'Sending request...' : 'Enviando solicitud...'}</span>
                        </>
                      ) : (
                        <>
                          <span>{f.submitBtn}</span>
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
